/**
 * Reading a simple site plan sketch into buildings and connections, in the
 * browser (Cleaning Visual, z8nrz7f731). The picture is a blueprint only: what
 * is read is proposed for review, and the drawn schematic is what is kept.
 *
 * What it understands is deliberately plain, a skeleton on a light background:
 *  - a building is a closed outline (any rectangle; its label written inside);
 *  - a connection is a line from one building to another: solid is a corridor,
 *    dashed a tunnel, double (two parallel lines) a bridge;
 *  - buildings drawn touching (sharing a wall) are adjacent: a corridor.
 *
 * Pure: pixels in, boxes out, no DOM, so the rules are tested on their own.
 */
import type { LinkKind } from '@/api/facilityApi'

/** RGBA pixels, as from a canvas's getImageData. */
export interface Raster {
  width: number
  height: number
  data: Uint8ClampedArray
}

/** A box in pixels, edges inclusive. */
export interface PixelBox {
  x0: number
  y0: number
  x1: number
  y1: number
}

export interface Skeleton {
  buildings: PixelBox[]
  /** By index into `buildings`. */
  connections: { a: number; b: number; kind: LinkKind }[]
}

/** How much darker than the background a pixel must be to be ink. */
const INK_CONTRAST = 60

export function readSkeleton({ width: W, height: H, data }: Raster): Skeleton {
  const n = W * H
  const minDim = Math.min(W, H)
  const lum = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    const a = data[i * 4 + 3]
    lum[i] = a < 128 ? 255 : 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2]
  }
  const bg = borderMedian(lum, W, H)
  const ink = new Uint8Array(n)
  for (let i = 0; i < n; i++) ink[i] = lum[i] < bg - INK_CONTRAST ? 1 : 0

  // Light regions not reachable from the picture's edge are the insides of closed outlines.
  const light = new Uint8Array(n)
  for (let i = 0; i < n; i++) light[i] = 1 - ink[i]
  const regions = components(light, W, H, false).filter((r) => !r.edge)
  const minArea = n * 0.001
  const enclosed = regions.filter((r) => r.area >= minArea && r.area / boxArea(r) >= 0.7 && boxArea(r) < n * 0.6)
  // A frame around several buildings is not a building.
  const shapes = enclosed.filter((r) => !enclosed.some((o) => o !== r && inside(o, r)))
  const outlined = shapes.map((r) => grow(r, strokeWidth(ink, W, H, r), W, H))
  // A long narrow strip closed off at both ends by buildings is the inside of a
  // double line between them (a bridge), not a building.
  const isPassage = (i: number) => {
    const r = shapes[i]
    const w = r.x1 - r.x0 + 1
    const h = r.y1 - r.y0 + 1
    if (Math.max(w, h) / Math.min(w, h) < 2.5) return false
    const ends = grow(outlined[i], 4, W, H)
    return outlined.filter((o, j) => j !== i && overlaps(ends, o)).length >= 2
  }
  const passageAt = shapes.map((_, i) => isPassage(i))
  const buildings = outlined.filter((_, i) => !passageAt[i]).sort((p, q) => p.y0 - q.y0 || p.x0 - q.x0)
  const passages = shapes.filter((_, i) => passageAt[i])

  // Everything inked outside the buildings (their outline and label included) may be a connection.
  const dilate = Math.max(3, Math.round(minDim * 0.012))
  const clear = dilate + 2
  const reach = clear + Math.max(4, Math.round(minDim * 0.015))
  const rest = ink.slice()
  for (const b of buildings) fill(rest, W, grow(b, clear, W, H), 0)
  // Which building a pixel is close enough to count as touching.
  const near = new Int16Array(n).fill(-1)
  buildings.forEach((b, i) => {
    const z = grow(b, reach, W, H)
    for (let y = z.y0; y <= z.y1; y++) for (let x = z.x0; x <= z.x1; x++) if (near[y * W + x] < 0) near[y * W + x] = i
  })

  const found = new Map<string, LinkKind>()
  const add = (touching: number[], box: PixelBox, kind: LinkKind) => {
    // A line along several buildings joins each to the next along it.
    const along = box.x1 - box.x0 >= box.y1 - box.y0 ? (i: number) => buildings[i].x0 + buildings[i].x1 : (i: number) => buildings[i].y0 + buildings[i].y1
    const ordered = [...touching].sort((p, q) => along(p) - along(q))
    for (let k = 1; k < ordered.length; k++) {
      const [a, b] = [ordered[k - 1], ordered[k]].sort((p, q) => p - q)
      const key = `${a}|${b}`
      const had = found.get(key)
      if (!had || had === 'corridor') found.set(key, kind)
    }
  }
  const touchingOf = (pixels: Iterable<number>) => {
    const set = new Set<number>()
    for (const p of pixels) if (near[p] >= 0) set.add(near[p])
    return [...set]
  }

  // A narrow closed strip between two buildings: a bridge drawn as two lines.
  for (const p of passages) {
    const touching = touchingOf(p.pixels)
    if (touching.length >= 2) add(touching, p, 'bridge')
  }

  const pieces = components(rest, W, H, true)
  const pieceOf = new Int32Array(n).fill(-1)
  pieces.forEach((pc, i) => {
    for (const p of pc.pixels) pieceOf[p] = i
  })
  for (const line of components(spread(rest, W, H, dilate), W, H, true)) {
    const touching = touchingOf(line.pixels)
    if (touching.length < 2) continue
    const sizes = new Map<number, number>()
    for (const p of line.pixels) if (pieceOf[p] >= 0) sizes.set(pieceOf[p], pieces[pieceOf[p]].area)
    const parts = [...sizes.values()].filter((s) => s >= 3).sort((p, q) => q - p)
    const total = parts.reduce((s, v) => s + v, 0)
    // Dashes: many pieces, none dominating. Two long pieces of a size: a double line.
    const kind: LinkKind =
      parts.length >= 4 && parts[0] < total * 0.4 ? 'tunnel' : parts.length >= 2 && parts[1] >= total * 0.3 ? 'bridge' : 'corridor'
    add(touching, line, kind)
  }

  // Adjacent: sharing a stretch of wall (drawn touching, or a few pixels apart); corners meeting do not count.
  buildings.forEach((p, i) =>
    buildings.forEach((q, j) => {
      if (j <= i || found.has(`${i}|${j}`)) return
      const gapX = Math.max(p.x0, q.x0) - Math.min(p.x1, q.x1)
      const gapY = Math.max(p.y0, q.y0) - Math.min(p.y1, q.y1)
      const minSide = Math.min(p.x1 - p.x0, p.y1 - p.y0, q.x1 - q.x0, q.y1 - q.y0)
      if ((gapX <= 3 && -gapY >= minSide * 0.25) || (gapY <= 3 && -gapX >= minSide * 0.25)) found.set(`${i}|${j}`, 'corridor')
    }),
  )

  const connections = [...found].map(([key, kind]) => {
    const [a, b] = key.split('|').map(Number)
    return { a, b, kind }
  })
  return { buildings: buildings.map(({ x0, y0, x1, y1 }) => ({ x0, y0, x1, y1 })), connections }
}

/**
 * A building's label as read inside it, into its code and name: "A" alone is
 * code A; "NW North Wing" or "NW\nNorth Wing" is code NW, name North Wing; a
 * longer first word is a name. Unreadable is "Building <n>".
 */
export function parseLabel(text: string, index: number): { code?: string; name: string } {
  const lines = text
    .split('\n')
    .map((l) => l.replace(/[^A-Za-z0-9&/\-'. ]+/g, ' ').replace(/\s+/g, ' ').trim())
    .filter((l) => /[A-Za-z0-9]/.test(l))
  const words = lines.join(' ').split(' ').filter(Boolean)
  if (words.length === 0) return { name: `Building ${index + 1}` }
  const first = words[0]
  const looksLikeCode = first.length <= 4 && /^[A-Z0-9][A-Z0-9-]*$/.test(first)
  if (looksLikeCode && words.length === 1) return { code: first, name: `Building ${first}` }
  if (looksLikeCode) return { code: first, name: words.slice(1).join(' ').slice(0, 120) }
  return { name: words.join(' ').slice(0, 120) }
}

/** A building's box in % of the picture, as the server takes it. */
export function toPercent(b: PixelBox, W: number, H: number) {
  const r = (v: number) => Math.round(v * 100) / 100
  return { x: r((b.x0 / W) * 100), y: r((b.y0 / H) * 100), width: r(((b.x1 - b.x0 + 1) / W) * 100), height: r(((b.y1 - b.y0 + 1) / H) * 100) }
}

interface Component extends PixelBox {
  area: number
  edge: boolean
  pixels: Int32Array
}

/** Connected runs of set pixels (4- or 8-connected), with their boxes. */
function components(mask: Uint8Array, W: number, H: number, diagonal: boolean): Component[] {
  const n = W * H
  const seen = new Uint8Array(n)
  const queue = new Int32Array(n)
  const out: Component[] = []
  for (let start = 0; start < n; start++) {
    if (!mask[start] || seen[start]) continue
    let head = 0
    let tail = 0
    queue[tail++] = start
    seen[start] = 1
    let x0 = W
    let y0 = H
    let x1 = 0
    let y1 = 0
    let edge = false
    while (head < tail) {
      const p = queue[head++]
      const x = p % W
      const y = (p - x) / W
      if (x < x0) x0 = x
      if (x > x1) x1 = x
      if (y < y0) y0 = y
      if (y > y1) y1 = y
      if (x === 0 || y === 0 || x === W - 1 || y === H - 1) edge = true
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if ((dx === 0 && dy === 0) || (!diagonal && dx !== 0 && dy !== 0)) continue
          const nx = x + dx
          const ny = y + dy
          if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue
          const q = ny * W + nx
          if (mask[q] && !seen[q]) {
            seen[q] = 1
            queue[tail++] = q
          }
        }
      }
    }
    out.push({ x0, y0, x1, y1, area: tail, edge, pixels: queue.slice(0, tail) })
  }
  return out
}

/** Every set pixel grown by `r` in each direction (a square brush). */
function spread(mask: Uint8Array, W: number, H: number, r: number): Uint8Array {
  const across = new Uint8Array(W * H)
  for (let y = 0; y < H; y++) {
    let last = -Infinity
    for (let x = 0; x < W; x++) {
      if (mask[y * W + x]) last = x
      if (x - last <= r) across[y * W + x] = 1
    }
    last = Infinity
    for (let x = W - 1; x >= 0; x--) {
      if (mask[y * W + x]) last = x
      if (last - x <= r) across[y * W + x] = 1
    }
  }
  const out = new Uint8Array(W * H)
  for (let x = 0; x < W; x++) {
    let last = -Infinity
    for (let y = 0; y < H; y++) {
      if (across[y * W + x]) last = y
      if (y - last <= r) out[y * W + x] = 1
    }
    last = Infinity
    for (let y = H - 1; y >= 0; y--) {
      if (across[y * W + x]) last = y
      if (last - y <= r) out[y * W + x] = 1
    }
  }
  return out
}

/** How thick the outline around a region's inside is, measured at the middle of each side. */
function strokeWidth(ink: Uint8Array, W: number, H: number, r: PixelBox): number {
  const my = (r.y0 + r.y1) >> 1
  const mx = (r.x0 + r.x1) >> 1
  const run = (x: number, y: number, dx: number, dy: number) => {
    let k = 0
    while (k < 12 && x >= 0 && y >= 0 && x < W && y < H && ink[y * W + x]) {
      k++
      x += dx
      y += dy
    }
    return k
  }
  return Math.max(run(r.x0 - 1, my, -1, 0), run(r.x1 + 1, my, 1, 0), run(mx, r.y0 - 1, 0, -1), run(mx, r.y1 + 1, 0, 1), 1)
}

function borderMedian(lum: Float32Array, W: number, H: number): number {
  const v: number[] = []
  for (let x = 0; x < W; x++) v.push(lum[x], lum[(H - 1) * W + x])
  for (let y = 0; y < H; y++) v.push(lum[y * W], lum[y * W + W - 1])
  v.sort((a, b) => a - b)
  return v[v.length >> 1]
}

const boxArea = (b: PixelBox) => (b.x1 - b.x0 + 1) * (b.y1 - b.y0 + 1)
const overlaps = (a: PixelBox, b: PixelBox) => a.x0 <= b.x1 && b.x0 <= a.x1 && a.y0 <= b.y1 && b.y0 <= a.y1
const inside = (inner: PixelBox, outer: PixelBox) => inner.x0 > outer.x0 && inner.y0 > outer.y0 && inner.x1 < outer.x1 && inner.y1 < outer.y1
const grow = (b: PixelBox, by: number, W: number, H: number): PixelBox => ({
  x0: Math.max(0, b.x0 - by),
  y0: Math.max(0, b.y0 - by),
  x1: Math.min(W - 1, b.x1 + by),
  y1: Math.min(H - 1, b.y1 + by),
})
function fill(mask: Uint8Array, W: number, b: PixelBox, v: number) {
  for (let y = b.y0; y <= b.y1; y++) mask.fill(v, y * W + b.x0, y * W + b.x1 + 1)
}
